import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileImage } from '@/common/constants';
import useFileErrorPopup from '@/components/controls/DropScan/hooks/useFileErrorPopup';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import useRemoveFileQuery from '@/modules/transaction/queries/useRemoveFile.query';
import { FileScanningPopup, PreviewFilePopup } from '@/modules/UseMyMoney/components';
import { useChangeClaimDocumentQuery, useGetDocumentOcrQuery, useGetDocumentsOpenaiOcrQuery } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore, useMyMoneyFormStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import { USE_MY_MONEY_UPLOAD_STORE_KEY, UseMyMoneyOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import SavedReceiptsPopupContainer from '@/modules/UseMyMoney/widgets/SavedReceipts/SavedReceiptsPopup.container';
import UploadFilePopupContainer from '@/modules/UseMyMoney/widgets/UploadFile/UploadFilePopup.container';

import useSynchronizeFilesInDocumentStore from './hooks/useSynchronizeFilesInDocumentStore';

enum UploadNewFileStep {
  SELECT_FILE = 'SELECT_FILE',
  PREVIEW_FILE = 'PREVIEW_FILE',
  FILE_SCANNING = 'FILE_SCANNING',
  SELECT_FROM_SAVED_RECEIPTS = 'SELECT_FROM_SAVED_RECEIPTS',
}

interface Props {
  storeKey: string;
  onSetVisible: (value: boolean) => void;
  onSuccessFileUpload?: () => void;
  setIsSuccessPopupVisible?: (value: boolean) => void;
}

const UploadNewFileContainer: React.FC<Props> = ({ storeKey, onSetVisible, onSuccessFileUpload, setIsSuccessPopupVisible }) => {
  const { t } = useTranslation();

  const { useMyMoneyOption } = useMyMoneyStore();
  const { getDocumentOcr, cancelOpenaiOcr } = useGetDocumentOcrQuery();
  const { getDocumentOpenaiOcr } = useGetDocumentsOpenaiOcrQuery();
  const { setOcrDocument, setIsOcrCompleted } = useDocumentOcrStore();
  const { removeFile } = useRemoveFileQuery();
  const { changeClaimDocument } = useChangeClaimDocumentQuery();

  // If the claim was created without a document, a new document will be created for it.
  // OCR scanning will be triggered for the newly created document.
  const { values: { createdClaimId }, handleSetState } = useMyMoneyFormStore();

  // Main claim store (source of truth) that contains the actual claim document and all uploaded files.
  const { values: { documentId } } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  // Temporary popup store used only while adding new files to the claim.
  // Its state is synchronized back to the main claim store.
  const { values: { files: newFiles }, handleSetValues, handleReset } = useFilesInDocumentStore(storeKey);

  const {
    fileUploadError,
    isFileUploadErrorModalVisible,
    onCloseFileUploadError,
    onFileUploadError,
  } = useFileErrorPopup();

  const [step, setStep] = useState(UploadNewFileStep.SELECT_FILE);

  const handleClose = useCallback(() => {
    onSetVisible(false);
    if (createdClaimId) {
      setIsSuccessPopupVisible?.(true);
    }
  }, [onSetVisible, createdClaimId, setIsSuccessPopupVisible]); 

  const handleOpenSelectFilePopup = useCallback(() => {
    setStep(UploadNewFileStep.SELECT_FILE);
  }, []);

  const handleOpenPreviewFilePopup = useCallback(() => {
    setStep(UploadNewFileStep.PREVIEW_FILE);
  }, []);

  const handleOpenFileScanningPopup = useCallback(() => {
    setStep(UploadNewFileStep.FILE_SCANNING);
  }, []);

  const handleOpenSavedReceiptsPopup = useCallback(() => {
    setStep(UploadNewFileStep.SELECT_FROM_SAVED_RECEIPTS);
  }, []);
  
  const handleSetFiles = useCallback((files: FileImage[]) => {
    handleSetValues({ files });
  }, [handleSetValues]);

  const handleDeleteFile = useCallback((fileId: string) => {
    handleSetValues({ files: newFiles.filter((file) => file.id !== fileId) });
    removeFile(fileId);
  }, [handleSetValues, removeFile, newFiles]);

  const handleScan = useCallback(async () => {
    if (useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL) {
      handleClose();
      return;
    }

    if (!documentId || !newFiles?.length) return;
    try {
      handleOpenFileScanningPopup();
      const documentOcr = await getDocumentOcr({ documentId });
      setOcrDocument(documentOcr);
      setIsOcrCompleted(true);
      handleClose();
    } catch {
      handleClose();
    }
  }, [
    handleOpenFileScanningPopup,
    getDocumentOcr,
    setOcrDocument,
    handleClose,
    setIsOcrCompleted,
    documentId,
    newFiles,
    useMyMoneyOption
  ]);

  const handleForCreatedScan = useCallback(async () => {
    if (!documentId || !createdClaimId || !newFiles?.length) return;
    try {
      handleOpenFileScanningPopup();
      const response = await getDocumentOpenaiOcr({ fileId: newFiles[0]?.id, claimId: createdClaimId });
      await changeClaimDocument({ documentId, claimId: createdClaimId });
      handleSetState({
        isClaimAutomaticallyApproved: !!response?.aiApproved,
        isClaimCreatedWithoutDocument: false,
      });
      onSuccessFileUpload?.();
      handleClose();
    } catch {
      handleClose();
    }
  }, [
    handleOpenFileScanningPopup,
    getDocumentOpenaiOcr,
    handleClose,
    changeClaimDocument,
    handleSetState,
    onSuccessFileUpload,
    documentId,
    newFiles,
    createdClaimId,
  ]);

  const handleCancelScanning = useCallback(() => {
    cancelOpenaiOcr();
    onSetVisible(false);
  }, [cancelOpenaiOcr, onSetVisible]);

  useEffect(() => {
    return () => {
      cancelOpenaiOcr();
    };
  }, [cancelOpenaiOcr]);

  useEffect(() => {
    return () => {
      handleReset();
    };
  }, [handleReset]);

  const { isReady } = useSynchronizeFilesInDocumentStore({ storeKey });

  if (!isReady) {
    return null;
  }

  return (
    <>
      {step === UploadNewFileStep.SELECT_FILE && (
        <UploadFilePopupContainer
          storeKey={storeKey}
          onUpload={handleOpenPreviewFilePopup}
          onQrCodeUpload={handleOpenPreviewFilePopup}
          onSetVisible={handleClose}
          onSelectFromSavedReceipts={handleOpenSavedReceiptsPopup}
          disableRemoveDocumentOnError
        />
      )}

      {step === UploadNewFileStep.PREVIEW_FILE && (
        <PreviewFilePopup
          title="Confirm Your Document Looks Correct"
          onSetVisible={handleClose}
          storeKey={storeKey}
          onBack={handleClose}
          onScan={createdClaimId ? handleForCreatedScan : handleScan}
          onDeleteFile={handleDeleteFile}
          onFileUploadError={onFileUploadError}
          onSetFiles={handleSetFiles}
        />
      )}

      {step === UploadNewFileStep.FILE_SCANNING && (
        <FileScanningPopup
          title={t('Looking for Claim Details…')}
          onSetVisible={handleCancelScanning}
          onCancelScanning={handleCancelScanning}
        />
      )}
      
      {step === UploadNewFileStep.SELECT_FROM_SAVED_RECEIPTS && (
        <SavedReceiptsPopupContainer onSetVisible={handleOpenSelectFilePopup} />
      )}

      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup
          fileError={fileUploadError}
          onClose={onCloseFileUploadError}
        />
      )}
    </>
  );
};

export default UploadNewFileContainer;
